// user.reducer.ts (maintains backward compatibility)

import { createReducer, on } from '@ngrx/store';
import { Models } from 'appwrite';
import { User } from 'src/app/models/user';
import { Subscription } from 'rxjs';
import { SystemProcess } from 'src/app/models/global-classes';
import { LoginMethodUIItem } from 'src/app/components/profile/security-settings/login-methods/login-methods.component';
import {
  SessionActions,
  LoginActions,
  TokenActions,
  UserProfileActions,
  WebhookActions,
  OnboardingActions,
  RegistrationActions,
} from './auth.actions';
import * as ProfileActions from '../profile/profile.actions';
import {
  passwordRecoveryRequestActions,
  passwordRecoveryConfirmActions,
} from '../password-forgot/password-forgot.actions';
import { loginMethodsUIBase } from '../data/login-methods';

// Maintain the exact same interface as before
export interface UserState {
  // User Profiles
  learmoProfile: User | undefined;
  appwriteProfile: Models.User<Models.Preferences> | undefined;

  // Processes
  profileUpdateProcess: SystemProcess;
  emailUpdateProcess: SystemProcess;
  passwordUpdateProcess: SystemProcess;
  enable2FaUpdateProcess: SystemProcess;
  loginMethodUpdateProcess: SystemProcess;
  passwordRecoveryProcess: SystemProcess;

  // Authentication
  token: string | undefined;
  subscription: Subscription | undefined;
  isRegistering: boolean;
  isEmailConfirmed: boolean | undefined;
  isEmailChanged: boolean;
  isRegistrationWebhookDone: boolean;
  loginMethods: LoginMethodUIItem[];
  error: any;

  tokenTimeout: number;
  isTokenRefreshScheduled: boolean;
}

export const initialState: UserState = {
  // User Profiles
  learmoProfile: undefined,
  appwriteProfile: undefined,

  // Processes
  profileUpdateProcess: new SystemProcess(),
  emailUpdateProcess: new SystemProcess(),
  passwordUpdateProcess: new SystemProcess(),
  enable2FaUpdateProcess: new SystemProcess(),
  loginMethodUpdateProcess: new SystemProcess(),
  passwordRecoveryProcess: new SystemProcess(),

  // Authentication
  token: undefined,
  subscription: undefined,
  isRegistering: false,
  isEmailConfirmed: undefined,
  isEmailChanged: false,
  isRegistrationWebhookDone: false,
  loginMethods: [],
  error: undefined,
  tokenTimeout: undefined,
  isTokenRefreshScheduled: false,
};

// User-related reducer with new actions but same interface
export const userReducer = createReducer(
  initialState,

  // ===== Session Management =====
  on(
    SessionActions.initializeSuccess,
    (state, { token, learmoProfile, appwriteProfile }) => ({
      ...state,
      token,
      learmoProfile,
      appwriteProfile,
    }),
  ),

  on(SessionActions.forget, () => initialState),

  // ===== Authentication and Profile updates =====
  on(UserProfileActions.setLearmoProfile, (state, { learmoProfile }) => {
    let avatar = prepareAvatarValue(learmoProfile);
    const updatedProfile = {
      ...learmoProfile,
      avatar,
    };

    return {
      ...state,
      learmoProfile: updatedProfile,
    };
  }),

  // Appwrite Profile update
  on(UserProfileActions.setAppwriteProfile, (state, { appwriteProfile }) => {
    return {
      ...state,
      appwriteProfile: appwriteProfile,
    };
  }),

  // Registration actions
  on(RegistrationActions.register, (state, {}) => ({
    ...state,
    isRegistering: true,
  })),

  on(RegistrationActions.verificationEmailSent, (state, {}) => ({
    ...state,
    isRegistering: false,
  })),

  // Authentication actions
  on(TokenActions.set, (state, { token }) => ({
    ...state,
    token,
  })),
  on(TokenActions.refreshSuccess, (state, { token }) => ({
    ...state,
    token,
  })),

  on(LoginActions.failure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(WebhookActions.complete, (state, {}) => ({
    ...state,
    isRegistrationWebhookDone: true,
  })),

  on(TokenActions.refresh, (state, {}) => ({
    ...state,
    isTokenRefreshScheduled: false,
    tokenTimeout: undefined,
  })),

  on(TokenActions.scheduleRefresh, (state, { timeout }) => ({
    ...state,
    isTokenRefreshScheduled: true,
    tokenTimeout: timeout,
  })),

  on(TokenActions.cancelScheduledRefresh, (state, {}) => ({
    ...state,
    isTokenRefreshScheduled: false,
    tokenTimeout: undefined,
  })),

  // Profile update actions (if you have ProfileActions)
  // Keeping structure for compatibility with existing profile actions
  on(UserProfileActions.updateProfile, (state, { profile }) => {
    return {
      ...state,
      profileUpdateProcess: { status: 'loading', error: undefined },
    };
  }),

  // Handle ProfileActions (from profile.actions.ts)
  on(ProfileActions.userProfileActions.update, (state) => ({
    ...state,
    profileUpdateProcess: { status: 'loading', error: undefined },
  })),

  on(UserProfileActions.updateProfileSuccess, (state, { profile }) => {
    let avatar = prepareAvatarValue(profile);
    return {
      ...state,
      learmoProfile: { ...state.learmoProfile, ...profile, avatar },
      profileUpdateProcess: { status: 'done', error: undefined },
    };
  }),

  on(
    ProfileActions.userProfileActions.updateSuccess,
    (state, { learmoProfile }) => {
      let avatar = prepareAvatarValue(learmoProfile);
      return {
        ...state,
        learmoProfile: { ...state.learmoProfile, ...learmoProfile, avatar },
        profileUpdateProcess: { status: 'done', error: undefined },
      };
    },
  ),

  on(UserProfileActions.updateProfileFailure, (state, { error }) => ({
    ...state,
    profileUpdateProcess: { status: 'error', error: error.message },
  })),

  on(ProfileActions.userProfileActions.updateFailure, (state, { error }) => ({
    ...state,
    profileUpdateProcess: { status: 'error', error: error.message },
  })),

  // Email update actions
  on(ProfileActions.userEmailActions.update, (state) => ({
    ...state,
    emailUpdateProcess: { status: 'loading', error: undefined },
  })),

  on(ProfileActions.userEmailActions.updateSuccess, (state, { response }) => ({
    ...state,
    learmoProfile: { ...state.learmoProfile, ...response },
    emailUpdateProcess: { status: 'done', error: undefined },
    isEmailChanged: true,
  })),

  on(ProfileActions.userEmailActions.updateFailure, (state, { error }) => ({
    ...state,
    emailUpdateProcess: {
      status: 'error',
      error: error.message,
      errorBody: error,
    },
  })),

  // 2FA actions
  on(ProfileActions.enable2FAActions.enable, (state) => ({
    ...state,
    enable2FaUpdateProcess: { status: 'loading', error: undefined },
  })),

  on(ProfileActions.enable2FAActions.enableSuccess, (state) => ({
    ...state,
    learmoProfile: {
      ...state.learmoProfile,
      twofa: 1,
    },
    enable2FaUpdateProcess: { status: 'done', error: undefined },
  })),

  on(ProfileActions.enable2FAActions.enableFailure, (state, { error }) => ({
    ...state,
    enable2FaUpdateProcess: { status: 'error', error: error.message },
  })),

  on(ProfileActions.enable2FAActions.disable, (state) => ({
    ...state,
    enable2FaUpdateProcess: { status: 'loading', error: undefined },
  })),

  on(ProfileActions.enable2FAActions.disableSuccess, (state) => ({
    ...state,
    learmoProfile: {
      ...state.learmoProfile,
      twofa: 0,
    },
    enable2FaUpdateProcess: { status: 'done', error: undefined },
  })),

  on(ProfileActions.enable2FAActions.disableFailure, (state, { error }) => ({
    ...state,
    enable2FaUpdateProcess: { status: 'error', error: error.message },
  })),

  // Login Method Config actions
  on(ProfileActions.userLoginMethodConfigActions.get, (state) => ({
    ...state,
    loginMethodUpdateProcess: { status: 'loading', error: undefined },
  })),

  on(
    ProfileActions.userLoginMethodConfigActions.getSuccess,
    (state, { loginMethodsResults }) => {
      const updatedMethods = loginMethodsUIBase.map((baseItem) => {
        const result = loginMethodsResults.find(
          (r) => r.method === baseItem.enum,
        );
        if (result) {
          return {
            ...baseItem,
            enabled: result.enabled,
            reason: result.reason,
          };
        }
        return baseItem;
      });
      return {
        ...state,
        loginMethods: updatedMethods,
        loginMethodUpdateProcess: { status: 'done', error: undefined },
      };
    },
  ),

  on(
    ProfileActions.userLoginMethodConfigActions.getFailure,
    (state, { error }) => ({
      ...state,
      loginMethodUpdateProcess: { status: 'error', error: error.message },
    }),
  ),

  on(ProfileActions.changeLoginMethodConfigActions.change, (state) => ({
    ...state,
    loginMethodUpdateProcess: { status: 'loading', error: undefined },
  })),

  on(
    ProfileActions.changeLoginMethodConfigActions.changeSuccess,
    (state, { method, enabled }) => {
      const updatedMethods = state.loginMethods.map((m) =>
        m.enum === method ? { ...m, enabled } : m,
      );
      return {
        ...state,
        loginMethods: updatedMethods,
        loginMethodUpdateProcess: { status: 'done', error: undefined },
      };
    },
  ),

  on(
    ProfileActions.changeLoginMethodConfigActions.changeFailure,
    (state, { error }) => ({
      ...state,
      loginMethodUpdateProcess: { status: 'error', error: error.message },
    }),
  ),

  // Website onboarding actions
  on(OnboardingActions.updateSuccess, (state, { isComplete }) => ({
    ...state,
    learmoProfile: !!state.learmoProfile
      ? {
          ...state.learmoProfile,
          onBoarding: {
            ...state.learmoProfile.onBoarding,
            websiteSetting: isComplete,
          },
        }
      : undefined,
  })),

  // ===== Password Recovery =====
  on(passwordRecoveryRequestActions.create, (state) => ({
    ...state,
    passwordRecoveryProcess: { status: 'loading', error: undefined },
  })),

  on(passwordRecoveryRequestActions.createSuccess, (state) => ({
    ...state,
    passwordRecoveryProcess: { status: 'done', error: undefined },
  })),

  on(passwordRecoveryConfirmActions.confirm, (state) => ({
    ...state,
    passwordRecoveryProcess: { status: 'loading', error: undefined },
  })),

  on(passwordRecoveryConfirmActions.confirmSuccess, (state) => ({
    ...state,
    passwordRecoveryProcess: { status: 'done', error: undefined },
  })),

  on(passwordRecoveryConfirmActions.confirmFailure, (state, { error }) => ({
    ...state,
    passwordRecoveryProcess: {
      status: 'error',
      error: error?.message || error,
    },
  })),
);

export function prepareAvatarValue(learmoProfile: User) {
  const randomNumber = Math.floor(Math.random() * 1000);

  if (
    !!learmoProfile?.avatar?.small &&
    !!learmoProfile?.avatar?.medium &&
    !!learmoProfile?.avatar?.big
  ) {
    return {
      small: learmoProfile.avatar.small + `?random=${randomNumber}`,
      medium: learmoProfile.avatar.medium + `?random=${randomNumber}`,
      big: learmoProfile.avatar.big + `?random=${randomNumber}`,
      customized: learmoProfile.avatar.customized,
    };
  } else {
    return {
      small: 'assets/images/users/blank.jpg',
      medium: 'assets/images/users/blank.jpg',
      big: 'assets/images/users/blank.jpg',
      customized: learmoProfile?.avatar?.customized ?? false,
    };
  }
}
